import React, { useState } from 'react';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import {
    Users,
    FilePlus,
    BarChart3,
    Upload,
    Clock,
    Plus,
    BookOpen,
    Search,
    MoreHorizontal,
    FileText,
    Image as ImageIcon,
    Video,
    X
} from 'lucide-react';

const managedCourses = [
    { id: 1, title: 'Mathematics - Class 10th A', students: 42, engagement: 88, color: '#6366F1' },
    { id: 2, title: 'Advanced Physics - Class 11th B', students: 35, engagement: 72, color: '#F59E0B' },
];

const initialActivity = [
    { id: 1, action: 'Uploaded material', target: 'Trigonometry Notes.pdf', time: '2 hours ago', icon: Upload, color: 'text-emerald-500 bg-emerald-50' },
    { id: 2, action: 'Assignment Created', target: 'Algebraic equations quiz', time: '5 hours ago', icon: FilePlus, color: 'text-indigo-500 bg-indigo-50' },
    { id: 3, action: 'Grade updated', target: 'Physics midterm results', time: 'Yesterday', icon: BarChart3, color: 'text-pink-500 bg-pink-50' },
];

export default function LMS() {
    const [showAddModal, setShowAddModal] = useState(false);
    const [activities, setActivities] = useState(initialActivity);
    const [newMaterial, setNewMaterial] = useState({ title: '', course: 'Mathematics - Class 10th A', type: 'PDF' });

    const handleAddMaterial = (e) => {
        e.preventDefault();
        const newAct = {
            id: Date.now(),
            action: 'Uploaded material',
            target: newMaterial.title || 'New Course Material',
            time: 'Just now',
            icon: Upload,
            color: 'text-emerald-500 bg-emerald-50'
        };
        setActivities([newAct, ...activities]);
        setShowAddModal(false);
        setNewMaterial({ title: '', course: 'Mathematics - Class 10th A', type: 'PDF' });
    };

    return (
        <div className="flex h-screen bg-[#F8FAFC] overflow-hidden font-sans">
            <Sidebar />

            <main className="flex-1 overflow-y-auto px-8">
                <Header
                    title="Teacher LMS"
                    subtitle="Manage your course curriculum and student materials"
                >
                    <button
                        onClick={() => setShowAddModal(true)}
                        className="flex items-center gap-2 px-6 py-2.5 bg-blue-500 text-white rounded-xl text-[14px] font-black hover:bg-blue-600 "
                    >
                        <Plus size={18} strokeWidth={3} />
                        ADD MATERIAL
                    </button>
                </Header>

                <div className="max-w-7xl mx-auto">
                    {/* Stats Overview */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                        <div className="bg-white p-6 rounded-[32px] border border-gray-100 shadow-sm transition-all hover:scale-[1.02]">
                            <div className="w-12 h-12 bg-indigo-50 text-indigo-500 rounded-2xl flex items-center justify-center mb-4">
                                <Users size={24} />
                            </div>
                            <h3 className="text-3xl font-black text-[#111827]">77</h3>
                            <p className="text-gray-400 font-bold text-xs uppercase tracking-widest mt-1">Total Students</p>
                        </div>
                        <div className="bg-white p-6 rounded-[32px] border border-gray-100 shadow-sm transition-all hover:scale-[1.02]">
                            <div className="w-12 h-12 bg-emerald-50 text-emerald-500 rounded-2xl flex items-center justify-center mb-4">
                                <BarChart3 size={24} />
                            </div>
                            <h3 className="text-3xl font-black text-[#111827]">82%</h3>
                            <p className="text-gray-400 font-bold text-xs uppercase tracking-widest mt-1">Avg Engagement</p>
                        </div>
                        <div className="bg-white p-6 rounded-[32px] border border-gray-100 shadow-sm transition-all hover:scale-[1.02]">
                            <div className="w-12 h-12 bg-amber-50 text-amber-500 rounded-2xl flex items-center justify-center mb-4">
                                <BookOpen size={24} />
                            </div>
                            <h3 className="text-3xl font-black text-[#111827]">12</h3>
                            <p className="text-gray-400 font-bold text-xs uppercase tracking-widest mt-1">Course Modules</p>
                        </div>
                        <div className="bg-white p-6 rounded-[32px] border border-gray-100 shadow-sm transition-all hover:scale-[1.02]">
                            <div className="w-12 h-12 bg-rose-50 text-rose-500 rounded-2xl flex items-center justify-center mb-4">
                                <FilePlus size={24} />
                            </div>
                            <h3 className="text-3xl font-black text-[#111827]">04</h3>
                            <p className="text-gray-400 font-bold text-xs uppercase tracking-widest mt-1">Pending Reviews</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-10">
                        {/* Managed Courses */}
                        <div className="lg:col-span-2">
                            <div className="flex items-center justify-between mb-6 px-2">
                                <h2 className="text-[20px] font-black text-[#111827] uppercase tracking-tighter">My Courses</h2>
                                <button className="text-blue-500 font-black text-xs uppercase tracking-widest hover:underline">View All</button>
                            </div>
                            <div className="space-y-4">
                                {managedCourses.map((course) => (
                                    <div
                                        key={course.id}
                                        className="bg-white p-6 rounded-[40px] border border-gray-100 shadow-sm hover:border-blue-500 transition-all cursor-pointer group"
                                    >
                                        <div className="flex items-start justify-between mb-4">
                                            <div>
                                                <h3 className="text-[18px] font-black text-[#111827] group-hover:text-blue-600 transition-colors uppercase leading-tight">{course.title}</h3>
                                                <div className="flex items-center gap-4 mt-2">
                                                    <span className="flex items-center gap-1.5 text-xs font-bold text-gray-400 uppercase">
                                                        <Users size={14} className="text-blue-500" />
                                                        {course.students} Students
                                                    </span>
                                                    <span className="flex items-center gap-1.5 text-xs font-bold text-gray-400 uppercase">
                                                        <BarChart3 size={14} className="text-emerald-500" />
                                                        {course.engagement}% Engagement
                                                    </span>
                                                </div>
                                            </div>
                                            <button className="p-2 text-gray-300 hover:text-gray-600"><MoreHorizontal size={20} /></button>
                                        </div>
                                        <div className="h-2 w-full bg-gray-50 rounded-full overflow-hidden">
                                            <div
                                                className="h-full rounded-full transition-all duration-1000"
                                                style={{ width: `${course.engagement}%`, backgroundColor: course.color }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Recent Activity */}
                        <div className="bg-white p-8 rounded-[40px] border border-gray-100 shadow-sm">
                            <h2 className="text-[20px] font-black text-[#111827] uppercase tracking-tighter mb-8 px-2">Recent Activity</h2>
                            <div className="space-y-8">
                                {activities.map((item) => (
                                    <div key={item.id} className="flex gap-5 group">
                                        <div className={`w-12 h-12 rounded-[18px] shrink-0 flex items-center justify-center transition-transform group-hover:scale-110 ${item.color}`}>
                                            <item.icon size={20} />
                                        </div>
                                        <div className="min-w-0">
                                            <h4 className="text-[14px] font-black text-[#111827] leading-none mb-1.5 uppercase tracking-tight">{item.action}</h4>
                                            <p className="text-[13px] font-bold text-gray-400 leading-tight mb-2 truncate">{item.target}</p>
                                            <div className="flex items-center gap-1.5 text-[10px] font-black text-gray-300 uppercase tracking-widest">
                                                <Clock size={12} />
                                                {item.time}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <button className="w-full mt-10 py-4 bg-gray-50 text-gray-500 rounded-2xl text-xs font-black uppercase tracking-[0.2em] hover:bg-[#111827] hover:text-white transition-all">
                                View Full History
                            </button>
                        </div>
                    </div>
                </div>
            </main>

            {/* Add Material Modal */}
            {showAddModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-[#111827]/40 backdrop-blur-sm" onClick={() => setShowAddModal(false)}></div>
                    <div className="bg-white w-full max-w-lg rounded-[48px] shadow-2xl relative z-10 overflow-hidden border border-gray-100 animate-in fade-in zoom-in duration-300">
                        <div className="p-10">
                            <div className="flex items-center justify-between mb-8">
                                <div>
                                    <h2 className="text-[24px] font-black text-[#111827] uppercase tracking-tighter">Add New Material</h2>
                                    <p className="text-gray-400 font-bold text-sm tracking-tight">Upload resources for your students</p>
                                </div>
                                <button
                                    onClick={() => setShowAddModal(false)}
                                    className="w-10 h-10 bg-gray-50 text-gray-400 rounded-full flex items-center justify-center hover:bg-rose-50 hover:text-rose-500 transition-all"
                                >
                                    <X size={20} strokeWidth={3} />
                                </button>
                            </div>

                            <form onSubmit={handleAddMaterial} className="space-y-6">
                                <div>
                                    <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest ml-1 mb-2 block text-left">Document Title</label>
                                    <input
                                        type="text"
                                        required
                                        value={newMaterial.title}
                                        onChange={(e) => setNewMaterial({ ...newMaterial, title: e.target.value })}
                                        placeholder="e.g. Trigonometry Basics"
                                        className="w-full h-14 px-6 bg-gray-50 border border-transparent rounded-2xl text-[15px] font-bold text-[#111827] focus:bg-white focus:border-blue-500 focus:outline-none transition-all"
                                    />
                                </div>

                                <div>
                                    <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest ml-1 mb-2 block text-left">Target Course</label>
                                    <select
                                        className="w-full h-14 px-6 bg-gray-50 border border-transparent rounded-2xl text-[15px] font-bold text-[#111827] focus:bg-white focus:border-blue-500 focus:outline-none transition-all appearance-none"
                                        value={newMaterial.course}
                                        onChange={(e) => setNewMaterial({ ...newMaterial, course: e.target.value })}
                                    >
                                        <option>Mathematics - Class 10th A</option>
                                        <option>Advanced Physics - Class 11th B</option>
                                    </select>
                                </div>

                                <div className="grid grid-cols-3 gap-4 mb-8">
                                    <button
                                        type="button"
                                        onClick={() => setNewMaterial({ ...newMaterial, type: 'PDF' })}
                                        className={`flex flex-col items-center gap-2 p-4 rounded-2xl border transition-all ${newMaterial.type === 'PDF' ? 'bg-indigo-50 border-indigo-500 text-indigo-600' : 'bg-white border-gray-100 text-gray-400'}`}
                                    >
                                        <FileText size={24} />
                                        <span className="text-[10px] font-black uppercase tracking-widest">PDF</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setNewMaterial({ ...newMaterial, type: 'Video' })}
                                        className={`flex flex-col items-center gap-2 p-4 rounded-2xl border transition-all ${newMaterial.type === 'Video' ? 'bg-emerald-50 border-emerald-500 text-emerald-600' : 'bg-white border-gray-100 text-gray-400'}`}
                                    >
                                        <Video size={24} />
                                        <span className="text-[10px] font-black uppercase tracking-widest">Video</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setNewMaterial({ ...newMaterial, type: 'Image' })}
                                        className={`flex flex-col items-center gap-2 p-4 rounded-2xl border transition-all ${newMaterial.type === 'Image' ? 'bg-rose-50 border-rose-500 text-rose-600' : 'bg-white border-gray-100 text-gray-400'}`}
                                    >
                                        <ImageIcon size={24} />
                                        <span className="text-[10px] font-black uppercase tracking-widest">Image</span>
                                    </button>
                                </div>

                                <button
                                    type="submit"
                                    className="w-full h-16 bg-blue-600 text-white rounded-3xl text-[16px] font-black uppercase tracking-widest shadow-xl shadow-blue-200 hover:bg-[#111827] hover:shadow-gray-200 transition-all active:scale-[0.98]"
                                >
                                    Publish Material
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
